import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, Trophy, Zap, Users, FileText } from "lucide-react";
import api, { API } from "@/lib/api";
import { brandOf, money, FusionMeter, Countdown, ShareControls, textOn, visibleOnDark } from "@/components/fusion/FusionShared";
import { PledgeDialog } from "@/components/fusion/PledgeDialog";
import { FusionVideoPlayer } from "@/components/fusion/FusionVideoPlayer";

export default function FusionAthletePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [pledgeOpen, setPledgeOpen] = useState(false);

  const load = () => api.get(`/fusion/public/athlete/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  if (err) return <Shell><div className="text-white/60 text-center pt-24">Campaign not found.</div></Shell>;
  if (!data) return <Shell><div className="text-white/40 text-center pt-24 animate-pulse">Loading campaign…</div></Shell>;

  const { event, athlete, team_totals, video, recent_supporters } = data;
  const b = brandOf(event);
  const safeSecondary = visibleOnDark(b.secondary);
  const url = window.location.href;
  const first = athlete.athlete_first;

  return (
    <Shell primary={b.primary}>
      {/* Team banner backdrop — tall, with the profile card overlapping it */}
      {b.teamPhoto && (
        <div className="relative w-full h-[300px] sm:h-[440px] overflow-hidden" data-testid="athlete-hero-banner">
          <img src={b.teamPhoto} alt={b.school} className="absolute inset-0 w-full h-full object-cover object-[center_30%]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,14,28,0.25) 0%, rgba(8,14,28,0.35) 55%, rgba(8,14,28,0.92) 88%, #080e1c 100%)" }} />
          <div className="absolute top-4 left-0 right-0 max-w-3xl mx-auto px-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {b.logo && <img src={b.logo} alt={b.school} className="h-11 w-11 object-contain rounded-lg bg-white/90 p-1 shadow" />}
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold drop-shadow" style={{ color: safeSecondary }}>BioHacker Fusion™</div>
                <div className="text-sm font-bold text-white drop-shadow" data-testid="fusion-school-name">{b.school}</div>
              </div>
            </div>
            <Link to={`/fusion/team/${event.slug}`} className="text-xs font-bold text-white/80 hover:text-white underline drop-shadow" data-testid="link-team-campaign">Team campaign →</Link>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 pb-24 relative z-10">
        {/* Header (no banner fallback) */}
        {!b.teamPhoto && (
          <header className="pt-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {b.logo && <img src={b.logo} alt={b.school} className="h-12 w-12 object-contain rounded-lg bg-white/90 p-1" />}
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: safeSecondary }}>BioHacker Fusion™</div>
                <div className="text-sm font-bold text-white/80" data-testid="fusion-school-name-plain">{b.school}</div>
              </div>
            </div>
            <Link to={`/fusion/team/${event.slug}`} className="text-xs font-bold text-white/50 hover:text-white underline" data-testid="link-team-campaign-plain">Team campaign →</Link>
          </header>
        )}

        {/* Hero */}
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className={`${b.teamPhoto ? "-mt-40 sm:-mt-56" : "mt-8"} rounded-3xl border border-white/10 overflow-hidden shadow-2xl`}
          style={{ background: `linear-gradient(135deg, ${b.primary}ee 0%, rgba(10,18,36,0.98) 70%)` }}>
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
            <div className="relative shrink-0">
              {athlete.photo_url ? (
                <img src={athlete.photo_url} alt={athlete.athlete_name} className="h-32 w-32 rounded-2xl object-cover border-2" style={{ borderColor: safeSecondary }} data-testid="athlete-photo" />
              ) : (
                <div className="h-32 w-32 rounded-2xl bg-white/10 flex items-center justify-center text-3xl font-black text-white/40">{first?.[0]}</div>
              )}
              {athlete.fundraising_rank && (
                <div className="absolute -bottom-2 -right-2 px-2 py-1 rounded-full text-[10px] font-black" style={{ background: safeSecondary, color: textOn(safeSecondary) }}>
                  #{athlete.fundraising_rank} fundraiser
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight" data-testid="athlete-name">{athlete.athlete_name}</h1>
              <div className="text-sm text-white/60 mt-1">{athlete.position ? `${athlete.position} · ` : ""}{event.team_name || b.school} · {event.name}</div>
              {athlete.personal_message && <p className="mt-3 text-sm text-white/75 italic">"{athlete.personal_message}"</p>}
              <div className="mt-4"><FusionMeter raised={athlete.raised_usd} goal={athlete.goal_usd} color={b.secondary} /></div>
              <div className="mt-3 flex gap-4 text-xs text-white/60">
                <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {athlete.sponsor_count} sponsors</span>
                {athlete.committed_per_ft_usd > 0 && <span className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> ${athlete.committed_per_ft_usd.toFixed(2)}/ft pledged</span>}
              </div>
            </div>
          </div>
          <div className="px-6 sm:px-8 pb-6 flex flex-wrap gap-3">
            <button onClick={() => setPledgeOpen(true)} data-testid="support-athlete-btn"
              className="px-6 py-3 rounded-full font-black text-sm transition-transform hover:scale-[1.03]"
              style={{ background: safeSecondary, color: textOn(safeSecondary) }}>
              SUPPORT {first?.toUpperCase()} · MAKE A PLEDGE
            </button>
          </div>
        </motion.section>

        {/* How sponsoring works — pledge encouragement */}
        <section className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="sponsor-how-it-works">
          {[
            { n: "1", t: "You pledge", d: `Per foot of ${first}'s best hit — or a flat donation right now.` },
            { n: "2", t: `${first} swings`, d: "Five official swings at the Fusion Hit-A-Thon. Best distance counts, verified by coaches." },
            { n: "3", t: "You pay for performance", d: "Invoiced only after the verified result. $0.25/ft × 220 ft = $55." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="h-7 w-7 rounded-full flex items-center justify-center font-black text-sm mb-2" style={{ background: safeSecondary, color: textOn(safeSecondary) }}>{s.n}</div>
              <div className="font-black text-white text-sm">{s.t}</div>
              <div className="text-xs text-white/55 mt-1 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </section>

        {/* Result */}
        {athlete.result_verified && (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="athlete-result-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: safeSecondary }}>
              <Trophy className="h-4 w-4" /> Verified Fusion result
            </div>
            <div className="mt-2 flex items-end gap-3">
              <div className="text-5xl font-black text-white">{athlete.best_distance_ft?.toFixed(0)}<span className="text-lg text-white/50 ml-1">ft</span></div>
              {athlete.performance_rank && <div className="text-xs font-bold text-white/60 pb-2">#{athlete.performance_rank} on the performance leaderboard</div>}
            </div>
            {athlete.swings_ft && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {athlete.swings_ft.map((s, i) => (
                  <div key={i} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70">Swing {i + 1}: <b className="text-white">{s} ft</b></div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* AI video */}
        {video && (
          <section className="mt-6">
            <FusionVideoPlayer photoUrl={athlete.photo_url} athleteName={athlete.athlete_name}
              school={b.school} logo={b.logo} color={b.secondary}
              assetId={video.asset_id} script={video.script} style={video.style}
              onPlay={() => api.post(`/fusion/public/athlete/${slug}/video-event?kind=view`).catch(() => {})} />
          </section>
        )}

        {/* Event details */}
        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col sm:flex-row gap-6 justify-between">
          <div className="space-y-2 text-sm text-white/70">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">Fusion Event</div>
            <div className="font-bold text-white">{event.name}</div>
            {event.event_date && <div className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" style={{ color: safeSecondary }} /> {new Date(event.event_date).toLocaleDateString(undefined, { dateStyle: "long" })}</div>}
            {event.location && <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: safeSecondary }} /> {event.location}</div>}
            {event.has_flyer && (
              <a href={`${API}/fusion/flyer/${event.event_id}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-1 px-3 py-2 rounded-full text-xs font-bold border transition-colors hover:bg-white/10"
                style={{ borderColor: safeSecondary, color: safeSecondary }} data-testid="athlete-view-flyer-btn">
                <FileText className="h-3.5 w-3.5" /> View fundraiser flyer
              </a>
            )}
          </div>
          <Countdown dateStr={event.event_date} color={safeSecondary} />
        </section>

        {/* Coach message */}
        {event.coach_message && (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 flex gap-3 items-start" data-testid="athlete-coach-message">
            {event.branding?.coach_photo_url && <img src={event.branding.coach_photo_url} alt="Coach" className="h-10 w-10 rounded-full object-cover" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-1">From the coach</div>
              <p className="text-sm text-white/75 italic">"{event.coach_message}"</p>
            </div>
          </section>
        )}

        {/* Team progress */}
        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-3">Team goal progress</div>
          <FusionMeter raised={team_totals.raised_usd} goal={team_totals.team_goal_usd} color={b.accent} testId="team-goal-meter" />
        </section>

        {/* Recent supporters */}
        {recent_supporters?.length > 0 && (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="recent-supporters">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-3">Recent supporters</div>
            <div className="space-y-2">
              {recent_supporters.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/80 font-semibold">{s.sponsor}</span>
                  <span className="text-white/50 text-xs">
                    {s.type === "flat" ? (s.amount_usd ? money(s.amount_usd) : "Donation") : `$${(s.rate_usd_per_ft || 0).toFixed(2)}/ft pledge`}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Share */}
        <section className="mt-6">
          <ShareControls url={url} title={`Support ${athlete.athlete_name} in the BioHacker Fusion Hit-A-Thon`}
            color={b.secondary}
            onShare={() => api.post(`/fusion/public/athlete/${slug}/video-event?kind=share`).catch(() => {})} />
        </section>

        <div className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
          BioHacker Fusion™ · Raise. Measure. Develop. Repeat.™
        </div>
      </div>

      <PledgeDialog open={pledgeOpen} onOpenChange={setPledgeOpen} slug={slug}
        athleteFirst={first} color={b.secondary} onCommitted={load} />

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 inset-x-0 sm:hidden p-3 backdrop-blur-lg" style={{ background: "rgba(8,14,28,0.85)" }}>
        <button onClick={() => setPledgeOpen(true)} className="w-full py-3 rounded-full font-black text-sm" style={{ background: safeSecondary, color: textOn(safeSecondary) }} data-testid="support-athlete-sticky-btn">
          MAKE A PLEDGE
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children, primary = "#0B1E36" }) {
  return (
    <div className="min-h-screen" style={{ background: `radial-gradient(1200px 500px at 50% -100px, ${primary}66, transparent), #080e1c` }}>
      {children}
    </div>
  );
}
